import { useState, useEffect, useRef } from "react";
import { useWatch } from "react-hook-form";
import { Button } from "@design-lab/shared/ui";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { ColorSelect } from "@design-lab/shared/ui/ColorSelect";
import { useProductColors } from "@design-lab/entities/product";
import { useCompositionsActions } from "@design-lab/entities/composition";
import { trackChangeColorClicked } from "@design-lab/shared/lib/analytics";
import { useArtworkForm } from "@design-lab/entities/artwork";
import { useClickOutside } from "@design-lab/shared/hooks/useClickOutside";

type ChangeShirtColorProps = {
  className?: string;
};

export function ChangeShirtColor(props: ChangeShirtColorProps) {
  const { className } = props;
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { colors, selectedColor, currentProduct, addedCompositionIds, recommendedColors } =
    useProductColors();
  const {
    selectCurrentComposition,
    replaceComposition,
    previewComposition,
    clearCompositionPreview
  } = useCompositionsActions();
  const { form } = useArtworkForm();
  const currentPlacement = useWatch({ control: form.control, name: "currentPlacement" });
  const side = currentPlacement === "back" ? "back" : "front";
  const artworkId = useWatch({ control: form.control, name: `${side}.artworkId` });
  const displayColor = selectedColor?.hex || "#000000";

  const handleButtonClick = () => {
    setIsPopoverOpen(!isPopoverOpen);
    if (artworkId) {
      trackChangeColorClicked({ source: "canvas", side, artworkId });
    }
  };

  const handleColorSelect = (id: string) => {
    if (!currentProduct) return;

    const compositionToAdd = currentProduct.compositions.find(comp => comp.id === id);
    if (!compositionToAdd) return;

    if (addedCompositionIds.has(id)) {
      selectCurrentComposition(id);
    } else {
      replaceComposition(id);
    }

    setIsPopoverOpen(false);
    clearCompositionPreview(); // Clear override when selecting
  };

  const handleColorHover = (id: string) => {
    previewComposition(id);
  };

  const handleColorBlur = () => {
    clearCompositionPreview();
  };

  // Close popover with ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsPopoverOpen(false);
        clearCompositionPreview(); // Clear override when closing
      }
    };

    if (isPopoverOpen) {
      document.addEventListener("keydown", handleEscKey);
      return () => {
        document.removeEventListener("keydown", handleEscKey);
      };
    }
    // Clear override when popover closes
    clearCompositionPreview();
    return undefined;
  }, [isPopoverOpen, clearCompositionPreview]);

  useClickOutside(containerRef, () => {
    setIsPopoverOpen(false);
    clearCompositionPreview();
  });

  return (
    <div ref={containerRef} className={cn("w-full", className)}>
      <Button
        data-testid="change-shirt-color-button"
        variant="outline"
        size="lg"
        aria-label="Change shirt color"
        onPress={handleButtonClick}
        className={cn("relative w-full", isPopoverOpen && "ring-2 ring-gray-700")}
      >
        <div className="flex items-center gap-0.5">
          {/* Paint Bucket Icon */}
          <svg
            className="mr-1 !h-[26px] !w-[26px]"
            viewBox="0 0 26 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M20.9902 15.9467C20.9902 15.9467 23.666 18.681 23.666 21.3958C23.666 23.1898 22.2089 24.6458 20.416 24.6458C18.622 24.6458 17.166 23.1898 17.166 21.3958C17.166 18.681 19.8408 15.9467 19.8408 15.9467C20.1582 15.6293 20.6728 15.6293 20.9902 15.9467Z"
              fill="#1F2937"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M19.9944 14.0118L13.866 20.1402C13.1044 20.9018 12.0709 21.3297 10.994 21.3297C9.91613 21.3297 8.88263 20.9018 8.12104 20.1402L3.52554 15.5447C2.76396 14.7821 2.33496 13.7486 2.33496 12.6717C2.33496 11.5938 2.76396 10.5603 3.52554 9.79873L10.4188 2.90548C10.7362 2.58807 11.2508 2.58807 11.5682 2.90548L19.9944 11.3306C20.7343 12.0716 20.7343 13.2719 19.9944 14.0118ZM18.845 12.8635C18.9511 12.7573 18.9511 12.5862 18.845 12.48L10.994 4.62907L4.67496 10.9481C4.21779 11.4053 3.95996 12.025 3.95996 12.6717C3.95996 13.3185 4.21779 13.9381 4.67496 14.3953L9.27046 18.9908C9.72763 19.448 10.3473 19.7047 10.994 19.7047C11.6397 19.7047 12.2605 19.448 12.7176 18.9908L18.845 12.8635Z"
              fill="#1F2937"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M12.8816 4.21849C13.1991 4.53591 13.1991 5.05049 12.8816 5.36791C12.5642 5.68424 12.0496 5.68424 11.7322 5.36791L9.10623 2.74083C8.78881 2.42449 8.78881 1.90883 9.10623 1.59249C9.42364 1.27508 9.93823 1.27508 10.2556 1.59249L12.8816 4.21849Z"
              fill="#1F2937"
            />
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M3.14746 11.8593H19.7365C20.185 11.8593 20.549 12.2233 20.549 12.6718C20.549 13.1571 20.3638 13.6424 19.9944 14.0118L13.866 20.1403C13.1044 20.9018 12.0709 21.3298 10.994 21.3298C9.91613 21.3298 8.88263 20.9018 8.12104 20.1403L3.52554 15.5448C2.76396 14.7821 2.33496 13.7486 2.33496 12.6718C2.33496 12.2233 2.69896 11.8593 3.14746 11.8593Z"
              fill="#1F2937"
            />
          </svg>
          Pick Color
        </div>

        {/* Color Indicator */}
        <div
          className="h-6 w-6 rounded-full border border-gray-300 shadow-sm"
          style={{ backgroundColor: displayColor }}
        />
      </Button>

      {/* Popover */}
      {isPopoverOpen && (
        <div
          className={cn(
            "absolute bottom-full mb-4",
            "left-1/2 -translate-x-1/2",
            "max-h-[400px] w-[280px] sm:max-h-[450px] sm:w-[350px] md:w-[500px]",
            "bg-background rounded-xl shadow-xl",
            "z-50 flex flex-col overflow-hidden"
          )}
        >
          <div className="flex-1 overflow-y-auto">
            <ColorSelect
              selectedColor={selectedColor}
              recommendedColors={recommendedColors}
              allColors={colors}
              onColorSelect={handleColorSelect}
              onColorHover={handleColorHover}
              onColorBlur={handleColorBlur}
              showAllColors={true}
              showAllRecommended={true}
              showToggleButton={false}
              product={currentProduct}
            />
          </div>
        </div>
      )}
    </div>
  );
}
